import React, { useContext, useState, useMemo, useEffect } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { Config } from 'configs';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  fillTranslatableTexts,
  getValidationPairsForTranslatableText,
  getValidationShapeForTranslatableText,
  TranslationFieldOptions,
} from 'utils/schemaHelper';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { ImageUpload } from 'components/formInputs/imageUpload';
import { files } from 'codemash';
import { formFileUrlFromFileArray } from 'utils/imageHelper';
import { FormButton } from 'components/buttons/formButton';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';

export const DeliverySettingsMainInfo = ({ itemData, isEdit }) => {
  const navigate = useNavigate();
  const { editById, addNew } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [bannerImage, setBannerImage] = useState([]);
  const [logoImage, setLogoImage] = useState([]);

  const schema = yup.object().shape(
    {
      _id: yup.string().default(itemData?._id),
      title: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.title,
            TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
          ),
          getValidationPairsForTranslatableText(),
        ),
      description: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.description,
            TranslationFieldOptions.OPTIONAL,
          ),
        ),
    },
  );

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData?._id) {
      if (itemData?.banner) {
        setBannerImage([{ data_url: formFileUrlFromFileArray(itemData?.banner, true) }]);
      }

      if (itemData?.logo) {
        setLogoImage([{ data_url: formFileUrlFromFileArray(itemData?.logo, true) }]);
      }
    }

    reset(itemData);
  }, [itemData, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    const submitedData = data;

    submitedData.title = fillTranslatableTexts(submitedData.title);
    submitedData.description = fillTranslatableTexts(submitedData.description);

    const sumbitData = { ...submitedData, company: auth.company?.company };

    if (!isEdit) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
          data: sumbitData,
          token: auth.token,
        },
        async (newId) => {
          if (bannerImage && bannerImage.length > 0) {
            await files.uploadRecordFile({
              collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
              database: Config.CODEMASH.DATABASE_ID,
              recordId: newId,
              uniqueFieldName: 'banner',
              secretKey: auth.token,
              file: bannerImage[0].file,
            });
          }

          if (logoImage && logoImage.length > 0) {
            await files.uploadRecordFile({
              collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
              database: Config.CODEMASH.DATABASE_ID,
              recordId: newId,
              uniqueFieldName: 'logo',
              secretKey: auth.token,
              file: logoImage[0].file,
            });
          }

          navigate(`/settings/delivery/${newId}`);
        },
        () => {
          setLoading(false);
        },
      );
    } else {
      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
          id: sumbitData._id,
          updateClause: {
            $set: {
              title: submitedData.title,
              description: submitedData.description,
            },
          },
          token: auth.token,
        },
        async () => {
          if (bannerImage && bannerImage.length > 0) {
            const uploadedNewImage =
              bannerImage[0].file !== null && bannerImage[0].file !== undefined;

            if (uploadedNewImage) {
              await files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
                database: Config.CODEMASH.DATABASE_ID,
                recordId: sumbitData._id,
                uniqueFieldName: 'banner',
                secretKey: auth.token,
                file: bannerImage[0].file,
              });
            }
          } else {
            await editById({
              collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
              id: sumbitData._id,
              updateClause: { $set: { banner: null } },
              token: auth.token,
            });
          }

          if (logoImage && logoImage.length > 0) {
            const uploadedNewImage =
              logoImage[0].file !== null && logoImage[0].file !== undefined;

            if (uploadedNewImage) {
              files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
                database: Config.CODEMASH.DATABASE_ID,
                recordId: sumbitData._id,
                uniqueFieldName: 'logo',
                secretKey: auth.token,
                file: logoImage[0].file,
              });
            }
          } else {
            await editById({
              collectionName: Config.DATA.COLLECTIONS.DELIVERY_SETTINGS,
              id: sumbitData._id,
              updateClause: { $set: { logo: null } },
              token: auth.token,
            });
          }
        },
        () => {
          setLoading(false);
        },
      );
    }
  };
  const onError = (data) => console.log(data);

  const onBannerImageChange = (imageList) => {
    setBannerImage(imageList);
  };

  const onLogoImageChange = (imageList) => {
    setLogoImage(imageList);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Main info</h3>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="sm:col-span-3 col-span-6">
              <TranslatableInput
                name="title"
                register={register}
                error={errors?.title}
                containerClassName="sm:col-span-3"
                labelClassName="block text-sm font-medium text-gray-700"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Title*"
              />
            </div>
            <div className="sm:col-span-3 col-span-6">
              <TranslatableInput
                name="description"
                register={register}
                error={errors?.description}
                containerClassName="sm:col-span-3"
                labelClassName="block text-sm font-medium text-gray-700"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Enter description"
                renderAsTextArea
              />
            </div>

            <div className="sm:col-span-3 col-span-6">
              <label htmlFor="logo" className="block text-sm font-medium text-gray-700 mb-1">
                Logo
              </label>
              <ImageUpload
                id="logo"
                name="logo"
                images={logoImage}
                onChange={onLogoImageChange}
              />
            </div>
            <div className="sm:col-span-3 col-span-6">
              <label htmlFor="banner" className="block text-sm font-medium text-gray-700 mb-1">
                Banner
              </label>
              <ImageUpload id="banner" name="banner" images={bannerImage} onChange={onBannerImageChange} />
            </div>

            <div className="col-span-4 sm:col-span-0 lg:col-span-4" />
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <FormButton text="Save" isLoading={loading} />
        </div>
      </div>
    </form>
  );
};
